import { Calendar, CalendarPlus, Clock, Inbox } from 'lucide-react';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { LEAVE_TYPE_LABEL } from '@/lib/leaveRequest.schema';
import type { LeaveRequestStatus, StudentLeaveRequest } from '@/types/leave';

/**
 * A child's leave requests and where each got to.
 *
 * Shared by the parent panel's Attendance screen and the student panel's Leave
 * Requests: the same rows, read by the family or by the child themselves. Only
 * the wording that names the reader changes, via `forSelf`.
 */

export interface LeaveRequestsCardProps {
  requests: StudentLeaveRequest[] | null;
  isLoading: boolean;
  error: ApiRequestError | null;
  onRetry: () => void;
  onApply?: () => void;
  onWithdraw: (request: StudentLeaveRequest) => void;
  /** The uuid currently being withdrawn, so only that row shows a spinner. */
  withdrawing: string | null;
  /** The reader is the child, not their family — "you", not "your child". */
  forSelf?: boolean;
}

const STATUS_TONE: Record<LeaveRequestStatus, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'critical',
  CANCELLED: 'neutral',
};

const STATUS_LABEL: Record<LeaveRequestStatus, string> = {
  PENDING: 'Waiting for the class teacher',
  APPROVED: 'Approved',
  REJECTED: 'Declined',
  CANCELLED: 'Withdrawn',
};

export function LeaveRequestsCard({
  requests,
  isLoading,
  error,
  onRetry,
  onApply,
  onWithdraw,
  withdrawing,
  forSelf = false,
}: LeaveRequestsCardProps) {
  if (error) {
    return (
      <div className="rounded-2xl sm:rounded-3xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <QueryError
          title="Could not load your requests"
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (!requests && isLoading) {
    return (
      <div className="rounded-2xl sm:rounded-3xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <SkeletonLines count={4} className="py-2" />
      </div>
    );
  }

  if (requests && requests.length === 0) {
    return (
      <div className="rounded-2xl sm:rounded-3xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No leave requests yet"
          description={
            forSelf
              ? "When you or your family ask for you to be excused, the request and the class teacher's answer both appear here."
              : "When you ask for your child to be excused, the request and the class teacher's answer both appear here."
          }
          action={
            onApply ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={onApply}
                leftIcon={<CalendarPlus className="h-4 w-4" />}
              >
                Apply for leave
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      {requests?.map((request) => (
        <div
          key={request.uuid}
          className="rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-sm hover:border-slate-400 dark:hover:border-slate-600 transition-colors flex flex-col gap-3"
        >
          {/* Line 1: Dates, Duration, Leave Type, Status Badge & Withdraw */}
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
              <div className="flex items-center gap-1.5 text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                <Calendar className="h-4 w-4 text-indigo-500 shrink-0" />
                <span>
                  {formatDate(request.fromDate)}
                  {request.fromDate !== request.toDate && ` – ${formatDate(request.toDate)}`}
                </span>
              </div>
              <span className="rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                {request.days} {request.days === 1 ? 'day' : 'days'}
              </span>
              <span className="text-slate-300 dark:text-slate-600 text-xs">•</span>
              <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2.5 py-0.5 rounded-full border border-indigo-200/60 dark:border-indigo-800/60">
                {LEAVE_TYPE_LABEL[request.type] ?? request.type}
              </span>
              {request.requestedOn && (
                <>
                  <span className="text-slate-300 dark:text-slate-600 text-xs">•</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Requested on {formatDate(request.requestedOn)}
                  </span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Badge tone={STATUS_TONE[request.status]} withDot>
                {STATUS_LABEL[request.status]}
              </Badge>
              {request.status === 'PENDING' && (
                <Button
                  size="sm"
                  variant="ghost"
                  isLoading={withdrawing === request.uuid}
                  onClick={() => onWithdraw(request)}
                  className="h-7 px-2.5 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg cursor-pointer"
                >
                  Withdraw
                </Button>
              )}
            </div>
          </div>

          {/* Line 2: Reason */}
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <span className="font-semibold text-slate-500 dark:text-slate-400">Reason: </span>
            {request.reason}
          </p>

          {/* Line 3: Teacher's Response (if decided) */}
          {request.decisionNote && (
             <>
            <div
              className={cn(
                'rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm',
                request.status === 'APPROVED'
                  ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-500/20'
                  : request.status === 'REJECTED'
                  ? 'bg-rose-50/40 dark:bg-rose-950/20 border-rose-500/20'
                  : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200/80 dark:border-slate-800',
              )}
            >
                <div className="flex justify-between gap-5 text-xs text-slate-500 dark:text-slate-400 leading-normal">
                  <p className="text-slate-800 dark:text-slate-200 font-medium">
                    <span className="font-bold text-slate-700 dark:text-slate-300 not-italic">Teacher response - </span><span className="italic">"{request.decisionNote}"</span>
                  </p>
                  <div className="shrink-0 whitespace-nowrap text-right">
                    <span>
                      {/* No name rather than a guessed one: `decidedBy` is null once
                          the decider's account is gone, and the school office may
                          have decided it, not the class teacher. */}
                      {request.decidedBy && <>{request.decidedBy} • </>}
                      {formatDate(request.decidedOn)}
                    </span>
                  </div>
                </div>
              </div>
             </>
          )}

          {/* Line 4: Pending Note */}
          {request.status === 'PENDING' && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium pt-0.5">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span>Waiting for the class teacher to review and respond</span>
            </div>
          )}

          {/* Line 5: Withdrawn Note */}
          {request.status === 'CANCELLED' && (
            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium pt-0.5">
              This request was withdrawn
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
